import type { Control, FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import type { Renter } from '../../utils/types/renter.interface';
import type { CoverageRuleId } from '@/shared/types';

import { useEffect, useMemo } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box, Checkbox, FormControlLabel, FormHelperText, Stack, Typography } from '@mui/material';

import { InfoBanner } from '@/components/InfoBanner';

import { useSplitFeatureFlag } from '@/hooks/useSplitFeatureFlag';
import { useCoverageRules } from '@/shared/hooks';
import { SplitFeatureFlag } from '@/utils/split/splitConfig';
import GroupRadioButton, {
  GroupRadioButtonSkeleton,
} from '../../components/input/GroupRadioButton';
import { ScreeningResultsTypes } from './ScreeningResultsTypes';
import { resolveOptionsByCoverageRules } from './utils/coverage.util';

type RadioInputBox<T extends CoverageRuleId | string> = {
  id: T;
  text: string;
  subText?: string;
  value: string;
  tooltip?: JSX.Element;
};

const screeningResultsTypesForSmb: RadioInputBox<string>[] = [
  {
    id: 'low',
    text: 'Low Coverage',
    subText: '2 months Rent Coverage, 1 month Deposit Coverage',
    value: ScreeningResultsTypes.Approved,
  },
  {
    id: 'medium',
    text: 'Medium Coverage',
    subText: '6 months Rent Coverage, 1 month Deposit Coverage',
    value: ScreeningResultsTypes.ApprovedWithConditions,
  },
  {
    id: 'max',
    text: 'Max Coverage',
    subText: '12 months Rent Coverage, 1 month Deposit Coverage',
    value: ScreeningResultsTypes.Denied,
  },
];

const screeningResultsOptions: RadioInputBox<CoverageRuleId>[] = [
  {
    id: 'approved',
    text: 'Approved',
    value: ScreeningResultsTypes.Approved,
    tooltip: (
      <Typography color="white" component={'span'} variant={'body2'}>
        Renters who qualify for a lease and would like to replace their security deposit.
      </Typography>
    ),
  },
  {
    id: 'conditional',
    text: 'Approved with Conditions',
    value: ScreeningResultsTypes.ApprovedWithConditions,
    tooltip: (
      <Typography color="white" component={'span'} variant={'body2'}>
        Renters who are replacing a larger security deposit, or need TheGuarantors uniform homes to
        help them qualify for the lease.
      </Typography>
    ),
  },
  {
    id: 'denied',
    text: 'Denied',
    value: ScreeningResultsTypes.Denied,
    tooltip: (
      <Typography color="white" component={'span'} variant={'body2'}>
        Renters who need TheGuarantors to help them qualify for the lease.
      </Typography>
    ),
  },
];

const disabledTooltip = (
  <Typography color="white" component="span" variant="body2">
    This property does not have default coverage selection for this status
  </Typography>
);

interface ScreeningResultsProps {
  control: Control<Renter, any>;
  errors: FieldErrors<Renter>;
  watch: UseFormWatch<Renter>;
  setValue: UseFormSetValue<Renter>;
  isSmbInvites: boolean;
}

export const ScreeningResults = ({
  control,
  errors,
  watch,
  setValue,
  isSmbInvites,
}: ScreeningResultsProps) => {
  const customCoverage = watch('customCoverage');
  const { isFeatureFlagEnabled: isDynamicCoveragesEnabled } = useSplitFeatureFlag(
    SplitFeatureFlag.OTTERS_4472_DYNAMIC_COVERAGES_INVITE,
  );

  const address = useWatch({ control, name: 'address' });
  const screeningResult = useWatch({ control, name: 'approvalStatus' });

  const addressSelected = Boolean(address?.monolith_building_id);
  const propertyId = isDynamicCoveragesEnabled ? address?.property_id : undefined;

  const { data: coverageRules, isLoading } = useCoverageRules(propertyId);

  const options = useMemo(
    () =>
      resolveOptionsByCoverageRules(screeningResultsOptions, coverageRules).map((opt) =>
        opt.disabled ? { ...opt, tooltip: disabledTooltip } : opt,
      ),
    [coverageRules],
  );

  useEffect(() => {
    if (
      !isSmbInvites &&
      !isLoading &&
      screeningResult &&
      options.some((opt) => opt.value === screeningResult && opt.disabled)
    ) {
      setValue('approvalStatus', '', { shouldValidate: true });
    }
  }, [isSmbInvites, isLoading, screeningResult, options, setValue]);

  const showSkeleton = isLoading && !isSmbInvites && addressSelected;
  const isLocked = isDynamicCoveragesEnabled && !isSmbInvites && !addressSelected;

  return (
    <>
      <Stack gap={4}>
        <Stack gap={1}>
          <Typography variant="h5" component="h2">
            {isSmbInvites ? 'Select your coverage' : 'Screening result'}
          </Typography>
          <Typography>
            {isSmbInvites
              ? `Based on your review of this renter at this time, please select or customize what level of
        coverage you require for your unit.*`
              : `Based on your review of this renter at this time, are they approved, approved with conditions, or denied? This can be changed later, but it will impact the quoted premium.`}
          </Typography>
          {isLocked && (
            <InfoBanner icon={<LockOutlinedIcon fontSize="inherit" />}>
              Please complete the lease information above first
            </InfoBanner>
          )}
        </Stack>
        <Stack gap={1.5}>
          {!isSmbInvites && <Typography>Select one *</Typography>}
          <Box>
            {showSkeleton ? (
              <GroupRadioButtonSkeleton
                count={screeningResultsOptions.length}
                dataTestId="screening-results-skeleton"
              />
            ) : (
              <Controller
                control={control}
                name="approvalStatus"
                render={({ field }) => (
                  <GroupRadioButton
                    id="approvalStatus"
                    dataTestId="approvalStatus"
                    options={isSmbInvites ? screeningResultsTypesForSmb : options}
                    field={field}
                    disabled={isLocked || (isSmbInvites && !!customCoverage)}
                  />
                )}
              />
            )}
            {errors.approvalStatus && (
              <FormHelperText error={true}>{errors.approvalStatus.message}</FormHelperText>
            )}
          </Box>
        </Stack>
      </Stack>
      {isSmbInvites && (
        <>
          <Box my={4}>
            <Controller
              name="customCoverage"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox {...field} checked={field.value} color="primary" sx={{ mx: 1 }} />
                  }
                  label="I want to customize my own coverage"
                />
              )}
            />
          </Box>
          <Box mb={4}>
            <Typography variant="helpertext">
              * Policy price paid by the renter is determined by gross rent, the length of coverage,
              and a renter's risk profile, decided by the landlord.
            </Typography>{' '}
          </Box>
        </>
      )}
    </>
  );
};
